import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { LineChart, Line, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { AlertTriangle, Info, CheckCircle2, Sparkles, TrendingUp } from "lucide-react";
import OnboardingChecklist from "@/components/OnboardingChecklist";

function Kpi({ label, value, unit, testId }) {
  return (
    <div className="border border-border p-6 bg-card" data-testid={testId}>
      <div className="overline text-muted-foreground">{label}</div>
      <div className="mt-3 font-display text-4xl font-bold tracking-tight">
        {value}<span className="text-muted-foreground text-2xl font-medium">{unit}</span>
      </div>
    </div>
  );
}

const alertIcon = { warning: AlertTriangle, info: Info, success: CheckCircle2 };
const alertColor = { warning: "text-amber-600", info: "text-primary", success: "text-emerald-600" };

export default function Dashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/dashboard/stats").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="p-8 font-mono-plex text-sm">Loading dashboard…</div>;
  const k = data.kpis;

  return (
    <div className="p-8 space-y-6" data-testid="dashboard-root">
      <OnboardingChecklist />
      <div className="flex items-baseline justify-between">
        <div>
          <div className="overline text-primary">/ EXECUTIVE COMMAND CENTER</div>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Overview</h1>
        </div>
        <div className="text-xs font-mono-plex text-muted-foreground uppercase tracking-widest">
          Live · {new Date().toLocaleDateString()}
        </div>
      </div>

      {/* Focus KPI + secondary */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="lg:col-span-2 lg:row-span-2 border border-border p-8 bg-card" data-testid="kpi-focus-oee">
          <div className="overline text-primary">FOCUS · OEE</div>
          <div className="mt-4 font-display text-7xl font-black tracking-tight">
            {k.oee_percent}<span className="text-muted-foreground text-4xl">%</span>
          </div>
          <div className="mt-2 flex items-center gap-2 text-sm text-emerald-600">
            <TrendingUp className="w-4 h-4" /> +2.4% vs last week
          </div>
          <div className="mt-6 h-40">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.trend_oee}>
                <XAxis dataKey="day" hide />
                <YAxis hide domain={[60, 100]} />
                <Tooltip cursor={{ stroke: "hsl(var(--border))" }} />
                <Line type="monotone" dataKey="value" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <Kpi label="SOPs Published" value={k.sops_published} unit="" testId="kpi-sops" />
        <Kpi label="Employees" value={k.employees} unit="" testId="kpi-employees" />
        <Kpi label="Quality Score" value={k.quality_score} unit="%" testId="kpi-quality" />
        <Kpi label="On-Time Delivery" value={k.on_time_delivery} unit="%" testId="kpi-otd" />
      </div>

      {/* Output + Alerts + AI */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 border border-border p-6 bg-card" data-testid="chart-output">
          <div className="flex items-baseline justify-between">
            <div className="overline text-muted-foreground">Production Output · 14d</div>
            <div className="font-mono-plex text-xs text-muted-foreground">units / day</div>
          </div>
          <div className="mt-4 h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.trend_output}>
                <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#71717A" }} />
                <YAxis tick={{ fontSize: 10, fill: "#71717A" }} />
                <Tooltip cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                <Bar dataKey="value" fill="hsl(var(--primary))" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border border-border p-6 bg-card" data-testid="alerts-panel">
          <div className="overline text-muted-foreground mb-4">Alerts</div>
          <div className="space-y-3">
            {data.alerts.map((a) => {
              const Icon = alertIcon[a.level] || Info;
              return (
                <div key={a.id} className="flex gap-3 items-start border-l-2 border-border pl-3 py-1">
                  <Icon className={`w-4 h-4 mt-0.5 ${alertColor[a.level]}`} />
                  <div className="text-sm leading-relaxed">{a.text}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* AI Insights */}
      <div className="border border-border bg-zinc-950 text-white p-6" data-testid="ai-insights-panel">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-4 h-4 text-accent" />
          <div className="overline text-accent">AI INSIGHTS · CLAUDE SONNET 4.6</div>
        </div>
        <ul className="space-y-3 font-mono-plex text-sm">
          {data.ai_insights.map((t, i) => (
            <li key={i} className="border-l-2 border-accent pl-3">{t}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}
